import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { AFFILIATE, IMG } from "../lib/api";
import AffiliateButton from "../components/AffiliateButton";
import EmailCapture from "../components/EmailCapture";
import { Utensils, Trees, Compass } from "lucide-react";

const sections = [
  {
    title: "Food & Drink",
    subtitle: "Riverside pubs, Italian legacy and afternoon tea.",
    icon: Utensils,
    img: IMG.pubFood,
    body:
      "Bedford has one of the largest Italian communities in the UK — which means its restaurant scene punches well above its weight. Start with riverside cocktails at The Embankment, then head to Bunyan's or Pizza Romana for dinner. For a classic British afternoon tea, book The Swan Hotel.",
    extraImg: IMG.afternoonTea1,
    cta: "Book Local Food Tours",
    href: AFFILIATE.getYourGuideActivities,
    testId: "things-food",
  },
  {
    title: "Parks & Outdoors",
    subtitle: "Rowing boats, riverside paths and wide open skies.",
    icon: Trees,
    img: IMG.riverHouses,
    body:
      "The Embankment is Bedford's pride — a tree-lined riverside promenade with rowing clubs, pedalos and one of England's prettiest suspension bridges. Further out, Priory Country Park offers 365 acres of lakes, woods and family walking trails within 10 minutes of the town centre.",
    extraImg: IMG.countryside,
    cta: "Find Outdoor Experiences",
    href: AFFILIATE.getYourGuide,
    testId: "things-parks",
  },
  {
    title: "Nearby Day Trips",
    subtitle: "Cambridge, Milton Keynes and the Chiltern Hills.",
    icon: Compass,
    img: IMG.cambridge,
    body:
      "Bedford is perfectly placed for day trips. Cambridge is 35 minutes by train — an essential detour for first-time UK visitors. Milton Keynes offers the UK's best indoor skydiving and Xscape snow centre. The Chiltern Hills are a 30-minute drive for walkers.",
    extraImg: IMG.marketSquare,
    cta: "Book Day Trips",
    href: AFFILIATE.getYourGuide,
    testId: "things-daytrips",
  },
];

const ThingsToDo = () => {
  useEffect(() => {
    document.title = "Things To Do in Bedford — Beyond Universal Studios | Visit Bedford";
  }, []);

  return (
    <div data-testid="things-to-do-page">
      {/* HERO */}
      <section className="relative h-[68vh] min-h-[480px] w-full overflow-hidden">
        <img src={IMG.afternoonTea2} alt="British afternoon tea" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="absolute inset-0 flex items-end">
          <div className="container-site pb-16 md:pb-20">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9 }}
              className="max-w-4xl"
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/15 backdrop-blur-md border border-white/25 text-white text-xs font-semibold tracking-[0.2em] uppercase mb-5">
                Beyond The Park
              </div>
              <h1 className="font-serif-display text-4xl md:text-6xl lg:text-7xl font-black text-white leading-[0.95] text-balance mb-5">
                Things to do in Bedford.
              </h1>
              <p className="text-white/90 text-lg md:text-xl max-w-2xl mb-8">
                Universal is the headline act — but Bedford rewards the unhurried traveller. Here's what locals actually recommend.
              </p>
              <AffiliateButton href={AFFILIATE.getYourGuide} variant="primary" testId="things-hero-cta">
                Find Experiences in Bedford
              </AffiliateButton>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Sections */}
      <div className="bg-[#FAFAFA]">
        {sections.map((s, i) => (
          <section
            key={s.title}
            data-testid={s.testId}
            className={`py-20 md:py-28 ${i % 2 === 1 ? "bg-white" : ""}`}
          >
            <div className="container-site grid grid-cols-1 lg:grid-cols-12 gap-10 md:gap-16 items-center">
              <div className={`lg:col-span-6 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                <motion.div
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.8 }}
                  className="grid grid-cols-2 gap-4"
                >
                  <div className="rounded-3xl overflow-hidden aspect-[3/4]">
                    <img src={s.img} alt={s.title} className="w-full h-full object-cover" />
                  </div>
                  <div className="rounded-3xl overflow-hidden aspect-[3/4] mt-10">
                    <img src={s.extraImg} alt={s.title} className="w-full h-full object-cover" />
                  </div>
                </motion.div>
              </div>
              <div className={`lg:col-span-5 ${i % 2 === 1 ? "lg:order-1 lg:col-start-2" : "lg:col-start-8"}`}>
                <div className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.25em] uppercase text-[#2AB7E8] mb-4">
                  <s.icon className="w-4 h-4" /> 0{i + 1}
                </div>
                <h2 className="font-serif-display text-4xl md:text-5xl font-bold text-[#0B2C5B] leading-tight mb-4">
                  {s.title}
                </h2>
                <p className="text-lg text-[#0B2C5B]/70 font-medium mb-5">{s.subtitle}</p>
                <p className="text-base md:text-lg leading-relaxed text-slate-700 mb-8">
                  {s.body}
                </p>
                <AffiliateButton href={s.href} variant="primary" testId={`${s.testId}-cta`}>
                  {s.cta}
                </AffiliateButton>
              </div>
            </div>
          </section>
        ))}
      </div>

      {/* Closing CTA */}
      <section className="bg-[#0B2C5B] text-white py-20 md:py-24">
        <div className="container-site text-center max-w-3xl mx-auto">
          <h2 className="font-serif-display text-4xl md:text-5xl font-bold mb-5 leading-tight">
            Book local activities &amp; experiences.
          </h2>
          <p className="text-lg text-white/80 mb-8">
            Boat trips on the Great Ouse, afternoon teas, brewery tours and Cambridge day trips — all bookable online.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <AffiliateButton href={AFFILIATE.getYourGuide} variant="primary" testId="things-final-gyg-cta">
              Browse on GetYourGuide
            </AffiliateButton>
            <AffiliateButton href={AFFILIATE.booking} variant="light" testId="things-final-booking-cta">
              Find a Hotel
            </AffiliateButton>
          </div>
        </div>
      </section>

      <EmailCapture source="things-to-do" />
    </div>
  );
};

export default ThingsToDo;
